import { X, FileText, Printer } from 'lucide-react';
import { useLegacyTranscripts } from '../hooks/useParticipantTranscripts';
import { LoadingSpinner } from '@lms/ui';
import { useLanguage } from '../lib/LanguageContext';
import { Bilingual } from './Bilingual';
import { copy } from '../copy/copy';

interface TranscriptModalProps {
  userId: string;
  userName: string;
  onClose: () => void;
}

export function TranscriptModal({ userId, userName, onClose }: TranscriptModalProps) {
  const { data: legacyData, isLoading } = useLegacyTranscripts(userId);
  const { language } = useLanguage();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-surface-container-lowest border border-on-surface/5 rounded-3xl max-w-4xl w-full max-h-[85vh] shadow-level4 flex flex-col animate-scaleUp">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-outline-variant/10">
          <div>
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-secondary/15 text-secondary">
                Riwayat Transkrip Masa Lalu
              </span>
              {legacyData?.user.userCode && (
                <span className="text-[10px] font-mono font-bold text-primary">
                  NIP: {legacyData.user.userCode}
                </span>
              )}
            </div>
            <h2 className="text-xl font-headline font-bold text-on-surface mt-1">
              <Bilingual {...copy.participantDetail.viewTranscript} />
            </h2>
            <p className="text-xs text-on-surface-variant">{userName}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              className="p-2 hover:bg-surface-container rounded-xl text-on-surface-variant transition-colors"
              title="Cetak Transkrip"
            >
              <Printer size={18} />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-surface-container rounded-xl text-on-surface-variant transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <LoadingSpinner size="lg" />
            </div>
          ) : !legacyData || legacyData.transcripts.length === 0 ? (
            <div className="text-center py-16 text-on-surface-variant">
              <FileText size={48} className="mx-auto mb-4 opacity-20" />
              <p className="text-sm italic">
                <Bilingual {...copy.participantDetail.noResults} />
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div className="p-4 rounded-2xl bg-surface-container-low border border-on-surface/5 space-y-1">
                  <span className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant opacity-70 block">
                    IPK Kumulatif
                  </span>
                  <p className="text-2xl font-headline font-black text-primary m-0">
                    {legacyData.summary.cumulativeGpa != null
                      ? legacyData.summary.cumulativeGpa.toFixed(2)
                      : '-'}{' '}
                    <span className="text-xs font-mono opacity-60">/ 4.00</span>
                  </p>
                  <span className="text-[10px] font-bold text-secondary">
                    {legacyData.summary.overallPredicate.letter} (
                    {legacyData.summary.overallPredicate.indonesian})
                  </span>
                </div>

                <div className="p-4 rounded-2xl bg-surface-container-low border border-on-surface/5 space-y-1">
                  <span className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant opacity-70 block">
                    Semester
                  </span>
                  <p className="text-2xl font-headline font-black text-on-surface m-0">
                    {legacyData.summary.totalSemesters}
                  </p>
                  <span className="text-[10px] text-on-surface-variant">Semester Selesai</span>
                </div>

                <div className="p-4 rounded-2xl bg-surface-container-low border border-on-surface/5 space-y-1">
                  <span className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant opacity-70 block">
                    Total SKS
                  </span>
                  <p className="text-2xl font-headline font-black text-secondary m-0">
                    {legacyData.summary.totalCredits} SKS
                  </p>
                  <span className="text-[10px] text-on-surface-variant">Beban Studi</span>
                </div>

                <div className="p-4 rounded-2xl bg-surface-container-low border border-on-surface/5 space-y-1">
                  <span className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant opacity-70 block">
                    Mata Kuliah Lulus
                  </span>
                  <p className="text-2xl font-headline font-black text-emerald-600 m-0">
                    {legacyData.summary.passedSubjects} / {legacyData.summary.totalSubjects}
                  </p>
                  <span className="text-[10px] text-emerald-700 font-bold">
                    {legacyData.summary.totalSubjects > 0
                      ? `${Math.round((legacyData.summary.passedSubjects / legacyData.summary.totalSubjects) * 100)}% Kelulusan`
                      : '-'}
                  </span>
                </div>
              </div>

              {/* Semesters & Subjects */}
              <div className="space-y-6">
                {legacyData.semesters.map(group => (
                  <div
                    key={group.semester}
                    className="border border-on-surface/5 rounded-2xl overflow-hidden bg-surface-container-low/20"
                  >
                    <div className="bg-surface-container-low px-4 py-3 flex justify-between items-center border-b border-on-surface/5">
                      <div className="flex items-center gap-2">
                        <span className="px-2.5 py-0.5 rounded-lg bg-primary text-on-primary text-xs font-bold font-mono">
                          Semester {group.semester}
                        </span>
                        <span className="text-xs font-headline font-bold text-on-surface">
                          {group.subjects.length} Mata Kuliah
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-xs font-body">
                        {group.semesterGpa != null && (
                          <span className="font-mono font-bold text-primary">
                            IPS: {group.semesterGpa.toFixed(2)}
                          </span>
                        )}
                        <span className="font-mono text-on-surface-variant font-medium">
                          {group.totalCredits} SKS
                        </span>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-xs font-body">
                        <thead>
                          <tr className="bg-surface-container-low/40 text-on-surface-variant border-b border-outline-variant/10 text-[10px] font-black uppercase tracking-wider">
                            <th className="px-4 py-2.5 w-10 text-center">No</th>
                            <th className="px-4 py-2.5">Mata Pelajaran / Mata Kuliah</th>
                            <th className="px-4 py-2.5 text-center">SKS</th>
                            <th className="px-4 py-2.5 text-center">Nilai Angka</th>
                            <th className="px-4 py-2.5 text-center">Nilai Huruf</th>
                            <th className="px-4 py-2.5 text-center">Predikat</th>
                            <th className="px-4 py-2.5 text-center">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-on-surface/5">
                          {group.subjects.map((s, idx) => (
                            <tr key={s.id} className="hover:bg-surface-container-low/40">
                              <td className="px-4 py-2.5 text-center font-mono opacity-60">
                                {idx + 1}
                              </td>
                              <td className="px-4 py-2.5">
                                <span className="font-headline font-bold text-on-surface block text-sm">
                                  {s.subjectName || s.program.name}
                                </span>
                                <div className="flex items-center gap-2 mt-0.5">
                                  {(s.subjectCode || s.program.code) && (
                                    <span className="text-[10px] font-mono text-primary font-bold">
                                      {s.subjectCode || s.program.code}
                                    </span>
                                  )}
                                  {s.programName && (
                                    <span className="text-[10px] text-on-surface-variant opacity-70">
                                      • {s.programName}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-2.5 text-center font-mono">{s.credit ?? 1}</td>
                              <td className="px-4 py-2.5 text-center font-mono font-bold">
                                {s.totalGrades != null ? s.totalGrades.toFixed(1) : '-'}
                              </td>
                              <td className="px-4 py-2.5 text-center font-headline font-bold text-primary">
                                {s.predicateDetails.letter}
                              </td>
                              <td className="px-4 py-2.5 text-center">
                                <span className="px-2 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-bold">
                                  {language === 'ar'
                                    ? s.predicateDetails.arabic
                                    : s.predicateDetails.indonesian}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-center">
                                <span
                                  className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${
                                    s.isCourseFinished
                                      ? 'bg-success/10 text-success'
                                      : 'bg-surface-container-high text-on-surface-variant'
                                  }`}
                                >
                                  {s.isCourseFinished ? 'LULUS' : 'BERJALAN'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
